import { useCallback, useEffect, useState } from "react";

/**
 * 画像拡縮 / ドラッグ移動 / ホイール / キーボード を共通化するフック。
 *
 * - ZoomableImageModal: 単体画像 Lightbox
 * - ImageCompareModal: モードタブ常時表示の compare 拡大モーダル
 *
 * `enabled=false` のときは keyboard / wheel / drag のグローバル購読を行わない。
 * モーダルが閉じている / 表示すべき画像が無いケースで `enabled=false` を渡す。
 *
 * `scale` / `position` は呼び出し側で `<img>` の `transform` に流し込む想定。
 */

export const MIN_SCALE = 0.5;
export const MAX_SCALE = 10;

export interface ImageZoomState {
  scale: number;
  position: { x: number; y: number };
  isDragging: boolean;
  // 直近の mousedown 以降に動いた最大距離（px）。
  // D&D 終端の mouseup→click による誤クリック検知に利用する。
  dragDistance: number;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleImageClick: (e: React.MouseEvent) => void;
  handleImageDoubleClick: (e: React.MouseEvent) => void;
  zoomIn: (e: React.MouseEvent) => void;
  zoomOut: (e: React.MouseEvent) => void;
  resetZoom: (e: React.MouseEvent) => void;
  resetPosition: (e: React.MouseEvent) => void;
}

export interface UseImageZoomOptions {
  /**
   * keyboard / wheel / drag のグローバル購読を有効にするか。
   * モーダルが閉じているときは false にして購読を止める。
   * onEscape も同期に enabled のときだけ呼ばれる。
   */
  enabled: boolean;
  /**
   * ESC キー押下時に呼ぶコールバック（モーダル close など）。
   * 渡さなければ何もしない。
   */
  onEscape?: () => void;
}

export function useImageZoom({ enabled, onEscape }: UseImageZoomOptions): ImageZoomState {
  const [scale, setScale] = useState<number>(1);
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [dragDistance, setDragDistance] = useState<number>(0);

  // enabled が false になったら状態をリセット
  useEffect(() => {
    if (!enabled) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
      setIsDragging(false);
      setDragDistance(0);
    }
  }, [enabled]);

  const zoomIn = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setScale((prev) => Math.min(prev + 0.25, MAX_SCALE));
  }, []);

  const zoomOut = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setScale((prev) => Math.max(prev - 0.25, MIN_SCALE));
  }, []);

  const resetZoom = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const resetPosition = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setPosition({ x: 0, y: 0 });
  }, []);

  // ESC / +,- / 0 のキーボードショートカット
  useEffect(() => {
    if (!enabled) return;
    const onKey = (event: KeyboardEvent) => {
      switch (event.key) {
        case "Escape":
          onEscape?.();
          break;
        case "+":
        case "=":
          setScale((prev) => Math.min(prev + 0.25, MAX_SCALE));
          break;
        case "-":
          setScale((prev) => Math.max(prev - 0.25, MIN_SCALE));
          break;
        case "0":
          setScale(1);
          setPosition({ x: 0, y: 0 });
          break;
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [enabled, onEscape]);

  // ホイール拡縮
  useEffect(() => {
    if (!enabled) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setScale((prev) => Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev + delta)));
    };
    document.addEventListener("wheel", onWheel, { passive: false });
    return () => document.removeEventListener("wheel", onWheel);
  }, [enabled]);

  // ドラッグ開始
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
      setDragDistance(0);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    },
    [position]
  );

  // ドラッグ中の move / up
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      e.preventDefault();
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      setPosition((prev) => {
        const distance = Math.sqrt(Math.pow(newX - prev.x, 2) + Math.pow(newY - prev.y, 2));
        setDragDistance((d) => Math.max(d, distance));
        return { x: newX, y: newY };
      });
    };
    const onUp = () => setIsDragging(false);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [isDragging, dragStart]);

  // 画像シングルクリック: 段階的拡大（1.0 → 1.5 → 2.0 → ... → 10.0 → 1.0）
  const handleImageClick = useCallback(
    (e: React.MouseEvent) => {
      // 5px 以上動いたらドラッグ扱い、クリック無視
      if (dragDistance > 5) return;
      e.preventDefault();
      e.stopPropagation();
      setScale((prev) => {
        if (prev < 1.5) return 1.5;
        if (prev < 2.0) return 2.0;
        if (prev < 2.5) return 2.5;
        if (prev < 3.0) return 3.0;
        if (prev < 4.0) return 4.0;
        if (prev < 6.0) return 6.0;
        if (prev < 8.0) return 8.0;
        if (prev < 10.0) {
          setPosition({ x: 0, y: 0 });
          return 10.0;
        }
        setPosition({ x: 0, y: 0 });
        return 1.0;
      });
    },
    [dragDistance]
  );

  // 画像ダブルクリック: 100% ↔ 200% トグル
  const handleImageDoubleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setScale((prev) => (prev === 1 ? 2 : 1));
    setPosition({ x: 0, y: 0 });
  }, []);

  return {
    scale,
    position,
    isDragging,
    dragDistance,
    handleMouseDown,
    handleImageClick,
    handleImageDoubleClick,
    zoomIn,
    zoomOut,
    resetZoom,
    resetPosition,
  };
}
